import qs from 'query-string'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Divider, Tag } from 'antd'
import { getRevisionHistoryUrl } from '@Builder/QuoteDetailPage/FilesTab/logic'
import { useGetOboDetails } from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'
import { HEADER_FILENAME_MAX_LENGTH } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'
import useImDfmOperations from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/logic'

import Box from '~/core/Box'
import Button from '~/core/Button'
import FileName from '~/core/FileName'
import Link from '~/core/Link'
import formatRevisionVersion from '~/lib/formatRevisionVersion'
import getColor from '~/lib/getColor'
import { ADMIN_PATH, QUOTES_PATH } from '~/redux-modules/preferences'

import DownloadDfmIcon from './DownloadDfmIcon'
import ImDfmModalHeaderAlert from './ImDfmModalHeaderAlert'
import imDfmModalHeaderQuery from './imDfmModalHeaderQuery'

const PLACEMENT = 'im-dfm-modal'

type ParamsType = {
    qmrId: string
    quoteId: string
    userId: string
}

type PropsType = {
    isImDfmAnnoSaving: boolean
    isInAnnotationMode: boolean
    onSaveImDfmAnnotations: () => void
    orderId?: string
    setIsModalVisible: (toggle: boolean) => void
    refetch?: () => void
    qmrId?: string
}

const ImDfmModalHeader = (props: PropsType) => {
    const {
        isImDfmAnnoSaving,
        isInAnnotationMode,
        onSaveImDfmAnnotations,
        setIsModalVisible,
        orderId,
        refetch,
    } = props

    const navigate = useNavigate()
    const { search } = useLocation()
    const { qmrId: urlQmrId } = useParams<ParamsType>()
    let qmrId = urlQmrId

    if (!qmrId) {
        qmrId = props?.qmrId
    }

    const { data } = useQuery(imDfmModalHeaderQuery, {
        variables: { id: qmrId },
        skip: !qmrId,
    })

    const { notifyFictivAdmin } = useImDfmOperations()

    const qmr = data?.quoteModelRevision
    const quoteUserId = qmr?.quoteModel?.quote?.user?.id
    const quoteId = qmr?.quoteModel?.quote?.id

    const { isObo, oboUserId } = useGetOboDetails(quoteUserId || '')

    const fileName = qmr?.fileName
    const version = qmr?.version
    const selectedQuoteModelRevisionId =
        qmr?.quoteModel?.selectedQuoteModelRevision?.id
    const totalImDfmFeedbacks = qmr?.dfmFeedback?.length

    const revisionHistoryUrl = getRevisionHistoryUrl({
        quoteId,
        qmrId: selectedQuoteModelRevisionId,
        isObo,
        oboUserId,
    })

    const isPreviousQmrRevision =
        qmr?.version !== qmr?.quoteModel?.selectedQuoteModelRevision?.version

    const showDownloadDfmIcon = !isPreviousQmrRevision && !isInAnnotationMode

    const onCancelModal = async () => {
        if (refetch) {
            await refetch()
        }
        const quoteDetailsPageUrl = isObo
            ? `${ADMIN_PATH}/user/${quoteUserId}/quotes/${quoteId}/feedback`
            : `${QUOTES_PATH}/${quoteId}/feedback`

        setIsModalVisible(false)
        navigate(quoteDetailsPageUrl)
    }
    const onCancel = () => {
        const searchParams = qs.parse(search)

        delete searchParams.mode

        navigate({ search: qs.stringify(searchParams) }, { replace: true })
    }

    const onClickSaveAndExit = async () => {
        await onSaveImDfmAnnotations()
        onCancel()
    }

    const onClickNotifyFictiv = () => {
        notifyFictivAdmin(quoteId, quoteUserId)
        onCancelModal()
    }

    return (
        <>
            <Box
                borderBottom={`1px solid ${getColor('blackAlpha15')}`}
                justifyContent="between"
                padding="16px"
            >
                <div>
                    <Tag style={{ height: 'fit-content' }}>
                        {formatRevisionVersion(version)}
                    </Tag>
                    <FileName
                        maxLength={HEADER_FILENAME_MAX_LENGTH}
                        name={fileName}
                        style={{
                            color: getColor('blackAlpha65'),
                            fontSize: '20px',
                            fontWeight: 500,
                        }}
                    />
                    {!isInAnnotationMode && (
                        <Link
                            data-test-target="view-revision-history-link"
                            from={{
                                backUrlFor: revisionHistoryUrl.backUrl,
                                quoteUserId,
                            }}
                            name="view-revision-history-link"
                            placement={PLACEMENT}
                            style={{
                                fontSize: '14px',
                                margin: 'auto 0 auto 8px',
                            }}
                            to={revisionHistoryUrl.url}
                        >
                            View revision history
                        </Link>
                    )}
                </div>
                <Box>
                    {isInAnnotationMode && (
                        <Box>
                            <Button
                                data-test-target="cancel-im-dfm-annotate-mode"
                                disabled={isImDfmAnnoSaving}
                                name="cancel-im-dfm-annotate-mode"
                                onClick={onCancel}
                                placement="im-dfm-modal"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={isImDfmAnnoSaving}
                                data-test-target="save-and-exit-im-dfm-annotate-mode"
                                loading={isImDfmAnnoSaving}
                                name="save-and-exit-im-dfm-annotate-mode"
                                onClick={onClickSaveAndExit}
                                placement="im-dfm-modal"
                                style={{ marginLeft: '4px' }}
                                type="primary"
                            >
                                {isImDfmAnnoSaving
                                    ? 'Saving and exiting'
                                    : 'Save and exit'}
                            </Button>
                        </Box>
                    )}
                    {showDownloadDfmIcon && (
                        <DownloadDfmIcon
                            totalImDfmFeedbacks={totalImDfmFeedbacks}
                            orderId={orderId}
                        />
                    )}
                    <Box style={orderId ? { marginRight: '50px' } : {}}>
                        <Divider
                            type="vertical"
                            style={{
                                height: '24px',
                                width: '8px',
                                top: '4px',
                            }}
                        />
                        <Button
                            data-test-target="return-to-quote-im-dfm-modal"
                            name="return-to-quote-im-dfm-modal"
                            onClick={onCancelModal}
                            placement="im-dfm-modal"
                        >
                            Return to quote
                        </Button>
                        <Button
                            data-test-target="notify-fictiv-im-dfm-modal"
                            name="notify-fictiv-im-dfm-modal"
                            onClick={onClickNotifyFictiv}
                            placement="im-dfm-modal"
                            style={{ marginLeft: '4px' }}
                            type="primary"
                        >
                            Close and Notify
                        </Button>
                    </Box>
                </Box>
            </Box>
            {isPreviousQmrRevision && (
                <Box width="100%">
                    <ImDfmModalHeaderAlert />
                </Box>
            )}
        </>
    )
}

export default ImDfmModalHeader
